
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import EvaVideo from "./EvaVideo";

import "./EvaGuidedTour.css";

import EvaDemoToolbar from "./EvaDemoToolbar";
import EvaBuildingViewer from "./EvaBuildingViewer";


//========================================================
// EVA Guided Tour
//========================================================

const TOUR_URL =
    "https://tour.giraffe360.com/49c74ed43a75417c96cf6a2684fac357";


//========================================================
// Introduction Script
//========================================================

const INTRODUCTION = `
Hello, and welcome to OmniScan Pro.

I'm Fiona, your virtual building survey guide.

Today, I'll show you how OmniScan transforms
the way we inspect and understand buildings.

Instead of carrying out separate surveys
for every discipline, we can explore a
building once and capture the evidence
relevant to each inspection.

Whether you're assessing fire safety,
building condition, mechanical and electrical
services, or accessibility, OmniScan can
guide you through the process.

We can identify assets, review documentation,
record observations and answer survey questions,
all while exploring the building.

And with our intelligent asset register,
we can bring relevant asset information
directly into the survey experience.

Now, let's choose a survey discipline
and begin exploring.
`;


//========================================================
// Survey Disciplines
//========================================================

const DISCIPLINES = [
    {
        id: "fire",
        title: "Fire Safety",
        description:
            "Fire protection, escape routes and compliance.",
        image: "/eva/images/fire-safety.jpg",
        accent: "#f16d70"
    },
    {
        id: "condition",
        title: "Building Condition",
        description:
            "Inspect building fabric, spaces and defects.",
        image: "/eva/images/building-condition.jpg",
        accent: "#73b8f5"
    },
    {
        id: "me",
        title: "Mechanical & Electrical",
        description:
            "Inspect services, equipment and maintenance.",
        image: "/eva/images/mechanical-electrical.jpg",
        accent: "#55d3c2"
    },
    {
        id: "accessibility",
        title: "Accessibility",
        description:
            "Assess access, circulation and facilities.",
        image: "/eva/images/accessibility.jpg",
        accent: "#f2d36b"
    }
];


//========================================================
// Audio Timing
//
// Our speech endpoint returns PCM16 audio.
//
// This assumes 16 kHz, mono, 16-bit PCM.
// If the backend uses another sample rate,
// change PCM_SAMPLE_RATE accordingly.
//
// The duration gives us a minimum safe window.
// Simli's silent event then provides the finish cue.
//========================================================

const PCM_SAMPLE_RATE = 16000;
const PCM_CHANNELS = 1;
const PCM_BYTES_PER_SAMPLE = 2;

const INTRODUCTION_PAUSE_MS = 1600;
const SPEECH_START_GRACE_MS = 5000;
const SPEECH_FINISH_TOLERANCE_MS = 500;


//========================================================
// Component
//========================================================

export default function EvaGuidedTour({
    onComplete
}) {

    //----------------------------------------------------
    // References
    //----------------------------------------------------

    const evaVideoRef = useRef(null);

    const mountedRef = useRef(false);

    const simliReadyRef = useRef(false);
    const introStartedRef = useRef(false);
    const heardSpeechRef = useRef(false);
    const speechSilentRef = useRef(false);
    const speechSentRef = useRef(false);

    const finishTimerRef = useRef(null);
    const safetyTimerRef = useRef(null);

    const speechStartTimeRef = useRef(null);
    const minimumFinishTimeRef = useRef(null);

    const completedRef = useRef(false);

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [stage, setStage] = useState("idle");

    const [status, setStatus] =
        useState("Ready to begin");

    const [simliReady, setSimliReady] =
        useState(false);

    const [introPlaying, setIntroPlaying] =
        useState(false);

    const [selectedDiscipline, setSelectedDiscipline] =
        useState(null);

    //----------------------------------------------------
    // Clear Timers
    //----------------------------------------------------

    const clearTimers = useCallback(() => {

        clearTimeout(finishTimerRef.current);
        clearTimeout(safetyTimerRef.current);

        finishTimerRef.current = null;
        safetyTimerRef.current = null;

    }, []);

    //----------------------------------------------------
    // Finish Introduction
    //----------------------------------------------------

    const finishIntroduction = useCallback(() => {

        if (completedRef.current) {
            return;
        }

        completedRef.current = true;

        clearTimers();

        setIntroPlaying(false);

        // Let the Fiona panel fade away first.

        setStage("transition");

        setTimeout(() => {

            if (mountedRef.current) {
                setStage("disciplines");
            }

        }, 650);

    }, [clearTimers]);

    //----------------------------------------------------
    // Schedule Completion
    //----------------------------------------------------

    const scheduleCompletion = useCallback(() => {

        if (
            completedRef.current ||
            !speechSentRef.current ||
            !heardSpeechRef.current ||
            !speechSilentRef.current
        ) {
            return;
        }

        const minimumFinishTime =
            minimumFinishTimeRef.current;

        if (!minimumFinishTime) {
            return;
        }

        const remainingMs =
            Math.max(
                0,
                minimumFinishTime - Date.now()
            );

        clearTimeout(finishTimerRef.current);

        finishTimerRef.current = setTimeout(() => {

            if (
                mountedRef.current &&
                !completedRef.current &&
                speechSilentRef.current
            ) {
                finishIntroduction();
            }

        }, remainingMs + INTRODUCTION_PAUSE_MS);

    }, [finishIntroduction]);

    //----------------------------------------------------
    // Simli Ready
    //----------------------------------------------------

    const handleReady = useCallback(() => {

        simliReadyRef.current = true;

        setSimliReady(true);
        setStatus("Fiona is ready");

    }, []);

    //----------------------------------------------------
    // Simli Speech Started
    //----------------------------------------------------

    const handleSpeechStart = useCallback(() => {

        if (
            !introStartedRef.current ||
            completedRef.current
        ) {
            return;
        }

        heardSpeechRef.current = true;
        speechSilentRef.current = false;

        clearTimeout(finishTimerRef.current);

        if (!speechStartTimeRef.current) {
            speechStartTimeRef.current = Date.now();
        }

        setStatus("Fiona is speaking");

    }, []);

    //----------------------------------------------------
    // Simli Speech Ended
    //----------------------------------------------------

    const handleSpeechEnd = useCallback(() => {

        if (
            !introStartedRef.current ||
            !heardSpeechRef.current ||
            completedRef.current
        ) {
            return;
        }

        speechSilentRef.current = true;

        scheduleCompletion();

    }, [scheduleCompletion]);

    //----------------------------------------------------
    // Begin Fiona's Introduction
    //----------------------------------------------------

    const beginIntroduction = useCallback(async () => {

        if (
            introStartedRef.current ||
            completedRef.current ||
            !simliReadyRef.current
        ) {
            return;
        }

        introStartedRef.current = true;

        setIntroPlaying(true);
        setStatus("Preparing Fiona's introduction");

        try {

            //------------------------------------------------
            // Generate Speech And Send To Simli
            //
            // EvaVideo.speak() generates the PCM audio
            // and sends it to Simli.
            //
            // For exact duration tracking, EvaVideo can
            // optionally return audio byte length.
            //------------------------------------------------

            const result =
                await evaVideoRef.current.speak(
                    INTRODUCTION.trim()
                );

            if (
                !mountedRef.current ||
                completedRef.current
            ) {
                return;
            }

            speechSentRef.current = true;

            //------------------------------------------------
            // PCM Duration
            //
            // EvaVideo.speak() in the existing implementation
            // returns undefined.
            //
            // If it returns audio byte count, use that.
            // Otherwise fall back to a conservative
            // estimate based on the spoken word count.
            //------------------------------------------------

            const byteLength =
                typeof result === "number"
                    ? result
                    : null;

            const words =
                INTRODUCTION.trim()
                    .split(/\s+/)
                    .length;

            const estimatedDurationMs =
                byteLength !== null
                    ? (
                        byteLength /
                        (
                            PCM_SAMPLE_RATE *
                            PCM_CHANNELS *
                            PCM_BYTES_PER_SAMPLE
                        )
                    ) * 1000
                    : (
                        words / 125
                    ) * 60000;

            const referenceTime =
                speechStartTimeRef.current ||
                Date.now();

            minimumFinishTimeRef.current =
                referenceTime +
                estimatedDurationMs -
                SPEECH_FINISH_TOLERANCE_MS;

            setStatus("Fiona is presenting");

            //------------------------------------------------
            // If Silent Already Fired, Schedule Completion
            //------------------------------------------------

            scheduleCompletion();

            //------------------------------------------------
            // Safety Check
            //
            // If Simli doesn't emit a final silent event,
            // don't leave the user stuck forever.
            //------------------------------------------------

            clearTimeout(safetyTimerRef.current);

            safetyTimerRef.current = setTimeout(() => {

                if (
                    mountedRef.current &&
                    !completedRef.current
                ) {
                    setStatus(
                        "Introduction complete"
                    );

                    finishIntroduction();
                }

            }, estimatedDurationMs +
               SPEECH_START_GRACE_MS +
               INTRODUCTION_PAUSE_MS +
               6000);

        }
        catch (exception) {

            console.error(
                "Unable to play guided introduction:",
                exception
            );

            introStartedRef.current = false;

            if (mountedRef.current) {
                setIntroPlaying(false);
                setStatus(
                    "Unable to play Fiona's introduction"
                );
            }

        }

    }, [finishIntroduction, scheduleCompletion]);

    //----------------------------------------------------
    // Start Guided Experience
    //----------------------------------------------------

    const startExperience = () => {

        if (stage !== "idle") {
            return;
        }

        setStage("welcome");
        setStatus("Connecting to Fiona");

    };

    //----------------------------------------------------
    // Start Speech Once Simli Is Ready
    //----------------------------------------------------

    useEffect(() => {

        if (
            stage === "welcome" &&
            simliReady &&
            !introStartedRef.current
        ) {
            beginIntroduction();
        }

    }, [
        stage,
        simliReady,
        beginIntroduction
    ]);

    //----------------------------------------------------
    // Skip Introduction
    //----------------------------------------------------

    const skipIntroduction = () => {

        finishIntroduction();

    };

    //----------------------------------------------------
    // Choose Discipline
    //----------------------------------------------------

    const chooseDiscipline = discipline => {

        setSelectedDiscipline(discipline);

        setStage("walkthrough");

    };

    //----------------------------------------------------
    // Return To Discipline Selection
    //----------------------------------------------------

    const changeDiscipline = () => {

        setStage("disciplines");

    };

    //----------------------------------------------------
    // Lifecycle
    //----------------------------------------------------

    useEffect(() => {

        mountedRef.current = true;

        return () => {

            mountedRef.current = false;

            clearTimers();

        };

    }, [clearTimers]);

    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div className="eva-demo-viewer">
		{/*===============================================
		  Giraffe360 Building
		===============================================*/}

		<EvaBuildingViewer />

            {/*===============================================
              EVA Branding
            ===============================================*/}

            <div className="eva-demo-brand">

                <div className="eva-demo-brand-name">
                    EVA
                </div>

                <div className="eva-demo-brand-subtitle">
                    Intelligent Estate Experience
                </div>

            </div>

            {/*===============================================
              Idle - Building Only
            ===============================================*/}

            {stage === "idle" && (

                <div className="eva-tour-start">

                    <div className="eva-tour-start-label">
                        OMNISCAN PRO
                    </div>

                    <h1>
                        Explore your building.
                        <br />
                        Discover more.
                    </h1>

                    <p>
                        Intelligent, AI-guided building
                        surveys and asset intelligence.
                    </p>

                    <button
                        type="button"
                        className="eva-tour-start-button"
                        onClick={startExperience}
                    >
                        <span className="eva-tour-play">
                            ▶
                        </span>

                        Start Guided Experience
                    </button>

                </div>

            )}

            {/*===============================================
              Fiona Welcome
            ===============================================*/}

            {(
                stage === "welcome" ||
                stage === "transition"
            ) && (

                <div
                    className={
                        "eva-intro-backdrop " +
                        (
                            stage === "transition"
                                ? "eva-intro-backdrop-exit"
                                : ""
                        )
                    }
                >

                    <div className="eva-intro-glass">

                        <div className="eva-intro-top">

                            <div>

                                <div className="eva-intro-eyebrow">
                                    OMNISCAN PRO / EVA
                                </div>

                                <h2>
                                    Intelligent Guided Survey
                                </h2>

                            </div>

                            <span className="eva-intro-pill">
                                AI GUIDED
                            </span>

                        </div>

                        <div className="eva-intro-video">

                            <EvaVideo
                                ref={evaVideoRef}
                                visible={true}
                                onReady={handleReady}
                                onSpeechStart={handleSpeechStart}
                                onSpeechEnd={handleSpeechEnd}
                            />

                            <div className="eva-intro-video-label">

                                <span className="eva-intro-audio-mark">
                                    ≋
                                </span>

                                <div>
                                    <strong>FIONA</strong>
                                    <small>
                                        Virtual Survey Guide
                                    </small>
                                </div>

                            </div>

                        </div>

                        <div className="eva-intro-message">

                            <h3>
                                Your building.
                                Your survey.
                                Your intelligence.
                            </h3>

                            <p>
                                Explore once. Capture evidence.
                                Unlock intelligent asset and
                                compliance information.
                            </p>

                        </div>

                        <div className="eva-intro-footer">

                            <div className="eva-intro-status">

                                <span className="eva-intro-pulse" />

                                {status}

                            </div>

                            <button
                                type="button"
                                className="eva-intro-secondary"
                                onClick={skipIntroduction}
                            >
                                Skip Introduction
                            </button>

                        </div>

                    </div>

                </div>

            )}

            {/*===============================================
              Discipline Selection
            ===============================================*/}

            {stage === "disciplines" && (

                <div className="eva-disciplines-backdrop">

                    <div className="eva-disciplines-heading">

                        <div className="eva-intro-eyebrow">
                            YOUR GUIDED EXPERIENCE
                        </div>

                        <h2>
                            Choose your survey discipline
                        </h2>

                        <p>
                            Select an inspection to begin
                            exploring your building.
                        </p>

                    </div>

                    <div className="eva-discipline-photo-grid">

                        {DISCIPLINES.map(discipline => (

                            <button
                                key={discipline.id}
                                type="button"
                                className="eva-discipline-photo-card"
                                style={{
                                    "--discipline-accent":
                                        discipline.accent
                                }}
                                onClick={() =>
                                    chooseDiscipline(
                                        discipline
                                    )
                                }
                            >

                                <div
                                    className="eva-discipline-photo"
                                    style={{
                                        backgroundImage:
                                            `url("${discipline.image}")`
                                    }}
                                />

                                <div className="eva-discipline-photo-content">

                                    <strong>
                                        {discipline.title}
                                    </strong>

                                    <span>
                                        {discipline.description}
                                    </span>

                                    <span className="eva-discipline-arrow">
                                        →
                                    </span>

                                </div>

                            </button>

                        ))}

                    </div>

                </div>

            )}

            {/*===============================================
              Active Survey Discipline
            ===============================================*/}

            {stage === "walkthrough" &&
                selectedDiscipline && (

                <div className="eva-intro-active-discipline">

                    <span className="eva-active-indicator" />

                    <strong>
                        {selectedDiscipline.title}
                    </strong>

                    <button
                        type="button"
                        onClick={changeDiscipline}
                    >
                        Change Discipline
                    </button>

                </div>

            )}
			
			
			{stage === "walkthrough" && (
			    <EvaDemoToolbar />
			)}

        </div>

    );

}
